// liveKeys.js — the live console's keys, and a reminder of the language.
// -----------------------------------------------
// The source for the console's cheatsheet (Ctrl+Shift+H, oscillaLive.js)
// and for the console's lines in the app's H shortcut list
// (keyboardShortcuts.js): one table, so neither drifts from the other.
// Pure data.

/** [keys, what they do] */
export const LIVE_KEYS = [
  ["Ctrl+Enter", "run the expression at the cursor (on a #$ line: its block); hold(s, 1) / gate(s, 0) then turn round for the next press; a trig:<signal> or follow(...) line run again unchanged switches off"],
  ["Ctrl+Alt+Enter", "run it on every voice of a poly: sound (Ctrl+Enter adds a voice)"],
  ["Ctrl+_", "freeze the line: its synths on their last note, its animations where they are (hold); again: go on"],
  ["Ctrl+Shift+Enter", "run everything, folded blocks too (with scene(): not the scenes)"],
  ["Alt+Enter", "on a #$ scene line: go to it, fading over 4 beats"],
  ["Ctrl+Shift+D", "the help page of the cue under the cursor, in a new tab"],
  ["Ctrl+.", "hush: asks first -- Enter kills every sound, Esc leaves it (Ctrl+. twice: at once)"],
  ["Ctrl+,", "still: asks first -- Enter stops every text and animation (the sound plays on), Esc leaves it"],
  ["Ctrl+Shift+,", "still(all): that, and the screen plain -- screen(off), every viz()'s effects off, trails wiped"],
  ["Ctrl+Shift+.", "stop what the line under the cursor started"],
  ["Tab", "take the faint completion -- a whole {…} too, reverb: or the keys left in one · on a #$ line: fold / unfold"],
  ["Ctrl+Space", "list the suggestions, to pick from"],
  ["Ctrl+Shift+K", "delete the line"],
  ["Ctrl+Shift+/", "comment the line (or the selection) out, or back in"],
  ["Ctrl+Shift+S", "stash the line (with Alt: the global stash)"],
  ["Ctrl+Shift+E", "the cue browser"],
  ["Ctrl+Shift+F", "perform mode: the code over the score"],
  ["Ctrl+Shift+B", "the block at the cursor alone; again: the line at the cursor alone; again: all (it follows the cursor)"],
  ["Ctrl+Shift+P", "the code on the projector window, or not"],
  ["Ctrl+Shift+M", "map:'s handles shown, or hidden for the show"],
  ["Ctrl+=  Ctrl+-", "the code's size"],
  ["Ctrl+Shift+L", "show / hide the console"],
  ["Ctrl+Shift+H", "this cheatsheet"],
];

/** [an example, what it is] */
export const LIVE_SYNTAX = [
  ["synth(uid:a, wave:saw, freq:220)", "a sound -- run it again to change it"],
  ["organ > rotate(dur:6)", "a cue on something named"],
  ["rotate(dur:3, axis:y)", "turn front to back (x: top over bottom)"],
  ["freq:pseq(220, 330, 440, inf)", "a pattern: pseq prand pxrand pshuf"],
  ["freq:pwhite(60, 190)", "anywhere in a range, each step"],
  ["freq:pwhite(40, 100) | scale(minor, root:A1)", "... through a pitch world: | notes([E, G, B]) | edo(19) | harm(27.5) | sieve(3@0 + 4@1); chained; even: each note as likely"],
  ["wave:choose(saw, square)", "one of them, each run · rand(5, 70)"],
  ["dur:0.25   tempo(120)", "dur is in beats; tempo is the beat"],
  ["envPerStep:{a:0.003, d:0.04, s:0}", "each step struck: choppy"],
  ["amp:follow(organ.sin, 0, 0.2)", "follow another's signal"],
  ["follow(bass.level, 0.6, 1.4)", "a sound's loudness · .hit .n · beat.phase"],
  ["pan:sinOsc(0.4, rand(0, 1))", "moves by itself: triOsc sawOsc pulseOsc lfNoise lfStep (rate, phase, lo, hi)"],
  ["hush(a, 6)", "fade a out over 6 beats · hush() stops all"],
  ["still()", "every animation and text stops · the sound plays on"],
  ["still(all)", "... and the screen plain: screen(off), every viz()'s effects off"],
  ["level(a, 0, 12, exp3)", "a's fader: down over 12 beats, still running · level(all, ...)"],
  ["xfade(a, b, 8)", "a down, b up, together"],
  ["hold(a)   hold(a, 0)", "freeze a's patterns where they are / let them go on"],
  ["seq(a, 0)   seq(a, 1)", "stop a's sequence after the note it is on (it dies away) / start it again on the beat"],
  ["gate(a, 0)   gate(a, 1, 4)", "let a go (its reverb rings on) / bring it back, over 4 beats"],
  ["viz(a, view:xy, palette:neon)", "how a sound is drawn"],
  ["#$ -- a block --", "heads a block: Tab folds it away; #$$ above several #$ blocks holds them all, #$$$ bigger again"],
  ["capture()", "record the window and its sound; again: stop (size:720 fps:60 wav:1)"],
  ["resources()", "a tiny HUD: cpu, memory, fps, audio late?, voices; again: hide"],
  ["screen(kaleido:6)  screen(off)", "the stage folded: kaleido mirror:x tile:3 · hue invert contrast blur pixel"],
  ["a > scatter(0.4, dur:2)", "a's paths thrown about · scatter(0) back · no target: the stage"],
  ["vu()  vu(bass, drums)", "meters: the mix, or each sound named · vu(all)"],
  ["// synth(uid:a, ...)", "commented out and run: a stops"],
];
